/**
 * One browser tab per client.
 *
 * Tabs are targeted by `window.name`. That name is assigned when the tab is
 * opened and then persists for the life of the browsing context — it is NOT
 * reset by navigation — so it has to be actively maintained or it goes stale:
 *
 * - A client tab that navigates back to the client list keeps its name, so
 *   re-clicking that same client would target the list tab itself and load the
 *   client in place instead of opening a tab. `AffiliateShell` clears the name
 *   on mount (see `clearClientTabName`).
 * - A tab navigated from client A to client B would still answer to A's name,
 *   letting a later click on A hijack a tab showing B. `SubjectProvider` keeps
 *   the name in step with the resolved `?id=` (see `setClientTabName`).
 *
 * With both in place the invariant is real rather than incidental: a tab named
 * `pnoe-client-<id>` is showing exactly that client.
 */

/** Single owner of the naming scheme — never build this string elsewhere. */
export const clientTabName = (clientId: string) => `pnoe-client-${clientId}`;

/** Claim this tab for a client, so re-clicks focus it instead of duplicating. */
export const setClientTabName = (clientId: string) => {
  window.name = clientTabName(clientId);
};

/** Release the claim — this tab no longer shows a single client's workspace. */
export const clearClientTabName = () => {
  window.name = "";
};

const openClientWindows = new Map<string, Window>();

/**
 * Open (or focus) a client's workspace tab.
 *
 * Must be called synchronously from a user-gesture handler (no preceding
 * `await`) or popup blockers will eat the tab.
 *
 * Returns false when the browser refused to open it, so the caller can say so —
 * a blocked popup is otherwise indistinguishable from a dead click.
 */
export const openClientTab = (clientId: string, url: string): boolean => {
  const existing = openClientWindows.get(clientId);
  if (existing && !existing.closed) {
    existing.focus();
    return true;
  }

  // Named target: one window per client. Deliberately no "noopener" — the
  // window reference is needed for focus() and both tabs are same-origin.
  const opened = window.open(url, clientTabName(clientId));
  if (!opened) {
    return false;
  }

  openClientWindows.set(clientId, opened);
  opened.focus();
  return true;
};
